import React, { useState, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import classNames from 'classnames';

//material ui
import { Button, Collapse } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { useFlag, useUnleashContext } from '@unleash/proxy-client-react';

import { FormattedMessageWrappedInSpan } from '../../misc';
import GDPRHint from '../GDPRHint';
import checkFileExtension from '../../../utils/checkFileExtension';
import { formatSnWithMaybeUndefinedVal } from '../../../utils/common';
import UploadGpxFileMutation from '../../../mutations/UploadGpxFileMutation';
import GetStravaAccessTokenMutation from '../../../mutations/GetStravaAccessTokenMutation';
import stravaButton from '../../../static/images/btn_strava_connect.svg'

import environment from '../../../environment';
import StravaActivitiesSelectionQueryRenderer from './StravaActivitiesSelectionQueryRenderer';

const ExtendedSearchGpsSearchTab = (props) => {
  const { eventId } = props;

  const [selectedFile, setSelectedFile] = useState(null);
  const [showFileExtensionError, setShowFileExtensionError] = useState(false);
  const [showFileSizeError, setShowFileSizeError] = useState(false);
  const [showSearchBtn, setShowSearchBtn] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [fileSelectedManually, setFileSelectedManually] = useState(false);
  const [isRedirectedFromStrava, setIsRedirectedFromStrava] = useState(false);
  const [lastGpsDataInoutType, setLastGpsDataInoutType] = useState();

  const [stravaAccessToken, setStravaAccessToken] = useState();
  const [showStravaAuthorizationError, setShowStravaAuthorizationError] = useState(false);
  const [showStravaNoActivitiesError, setShowStravaNoActivitiesError] = useState(false);
  const [stravaActivityData, setStravaActivityData] = useState({
    stravaActivityId: '',
    stravaActivityStartDate: '',
  });
  const location = useLocation();
  const history = useHistory();
  const params = useParams();

  const isStravaSearchAvailable = useFlag('strava');
  const updateContext = useUnleashContext();

  const noStravaActivitiesFoundCallback = () => {
    setShowStravaNoActivitiesError(true);
  };

  const availableStravaActivityId = stravaActivityData.stravaActivityId || params.stravaActivityId;

  const setUpStravaActivity = (newActivityData) => {
    setLastGpsDataInoutType('strava');
    setFileSelectedManually(false);
    setStravaActivityData({
      stravaActivityId: newActivityData.stravaActivityId,
      stravaActivityStartDate: newActivityData.stravaActivityStartDate,
    });
    setShowSearchBtn(true);
  };

  // Unleash context is updated with event ID
  useEffect(() => {
    updateContext({ userId: 'event-' + eventId });
  }, [eventId, updateContext]);

  //after Strava authorization
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const authorizationCode = searchParams.get('code');
    if (authorizationCode && !stravaAccessToken) {
      setIsRedirectedFromStrava(true);
      GetStravaAccessTokenMutation.commit(
        environment,
        authorizationCode,
        (error, response) => {
          if (!error) {
            setStravaAccessToken(response.accessToken);
            setShowStravaAuthorizationError(false);
            setShowStravaNoActivitiesError(false);
            setLastGpsDataInoutType('strava');
            history.replace({
              pathname: location.pathname,
              search: '',
            });
          } else {
            setShowStravaAuthorizationError(true);
          }
        }
      );
    }
  }, [location.search, stravaAccessToken]);

  const handleConnectToStrava = () => {
    setShowSearchBtn(false);

    // TODO add refresh token for previous search if backend supports this feature
    // for now, previous search results are cleaned
    const pathname = window.location.pathname;
    let cleanPathname;
    const isStrava = window.location.pathname.includes('strava');

    if (isStrava) {
      const gpsParamsStartIndex = pathname.indexOf('/gps');
      cleanPathname = pathname.substring(0, gpsParamsStartIndex);
    }

    const redirectUri =
      window.location.origin + (cleanPathname ? cleanPathname : window.location.pathname);
      
    // TODO remove read_all scope to only ask for activity access
    const scope = 'activity:read_all';

    const authUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}`;
    window.location.href = authUrl;
  };

  const handleFileSelectedManuallyCallback = () => {
    setFileSelectedManually(false);
  };

  const handleFileChanged = (event, type) => {
    let selectedFile;
    selectedFile = event.target.files[0];
    setLastGpsDataInoutType(type);

    // 'selectedFile' can be undefined if no file has been selected in the file manager
    if (selectedFile) {
      setFileSelectedManually(true);
      // Check if the file selected by the user is a "gpx" file)
      if (checkFileExtension(selectedFile.name, 'gpx')) {
        // The backend currently supports file uploads up to 20.971.520 bytes (i.e. 20 MB)
        if (selectedFile.size > 20971520) {
          setSelectedFile(null);
          setShowFileExtensionError(false);
          setShowFileSizeError(true);
          setShowSearchBtn(false);
          return;
        }
        setSelectedFile(selectedFile);
        setShowFileExtensionError(false);
        setShowFileSizeError(false);
        setShowSearchBtn(true);
      } else {
        setSelectedFile(null);
        setShowFileExtensionError(true);
        setShowFileSizeError(false);
        setShowSearchBtn(false);
      }
    }
  };

  const handleUploadGpxFile = () => {
    const { eventId, startNumber, onNavigateTo } = props;
    let formattedstartNumber = formatSnWithMaybeUndefinedVal(startNumber);
    if (
      !fileSelectedManually &&
      stravaAccessToken &&
      Boolean(stravaActivityData.stravaActivityId) &&
      Boolean(stravaActivityData.stravaActivityStartDate)
    ) {
      onNavigateTo(
        `/event/${eventId}/gps/strava/${stravaActivityData.stravaActivityId}/${window.btoa(stravaActivityData.stravaActivityStartDate)}/${formattedstartNumber}/?codeStrava=${stravaAccessToken}`
      );
    } else if (selectedFile) {
      setIsUploading(true);
      UploadGpxFileMutation.commit(environment, selectedFile, (error, fileId) => {
        if (error) {
          setIsUploading(false);
        } else {
          setIsUploading(false);
          onNavigateTo(`/event/${eventId}/gps/${fileId}/${formattedstartNumber}`);
        }
      });
    }
  };

  return (
    <div className="extended-search-gpssearch-container">
      <div>
        <h2>
          <FormattedMessageWrappedInSpan
            id="extendedSearchGpsSearchTab.ownSearch.title"
            defaultMessage="Search with your GPS data"
          />
        </h2>
        <p>
          {isStravaSearchAvailable ? (
            <FormattedMessageWrappedInSpan
              id="extendedSearchGpsSearchTab.ownSearch.descriptionWithStrava"
              defaultMessage="Upload your GPX file manually from the tracking device or connect to Strava and choose an activity and we will show your photos"
            />
          ) : (
            <FormattedMessageWrappedInSpan
              id="extendedSearchGpsSearchTab.ownSearch.descriptionWithoutStrava"
              defaultMessage="Upload your GPX file and we will show your photos"
            />
          )}
        </p>
        <div className="gps-search-columns">
          {/* manual upload */}
          <div
            className={classNames('gps-search-column', {
              'gps-search-column_selected': lastGpsDataInoutType === 'manual',
            })}
          >
            <Collapse
              in={
                (!selectedFile || isUploading) &&
                !params.stravaActivityId &&
                !stravaAccessToken
              }
              data-test-id="manual-upload-options"
            >
              <span className="gps-search-column-label">
                {!showFileExtensionError && !showFileExtensionError && !isUploading && (
                  <FormattedMessageWrappedInSpan
                    id="extendedSearchGpsSearchTab.ownSearch.uploadManuallyDescription"
                    defaultMessage="Upload GPX manually"
                    className="typography bold-text"
                  />
                )}
                {showFileExtensionError && (
                  <FormattedMessageWrappedInSpan
                    id="extendedSearchGpsSearchTab.ownSearch.fileExtensionError"
                    defaultMessage="The file should have the following extension: .gpx"
                    className="typography warning-text bold-text"
                  />
                )}
                {showFileSizeError && (
                  <FormattedMessageWrappedInSpan
                    id="extendedSearchGpsSearchTab.ownSearch.fileSizeError"
                    defaultMessage="The file is too big. We currently support file uploads up to 20 MB."
                    className="typography warning-text bold-text"
                  />
                )}
                {isUploading && <CircularProgress size={24} thickness={3} />}
              </span>
            </Collapse>
            {params.stravaActivityId || stravaAccessToken ? (
              <FormattedMessageWrappedInSpan
                id="extendedSearchGpsSearchTab.ownSearch.redirectFromStravaInstruction"
                defaultMessage="To be able to upload own file, go back to main search."
              />
            ) : (
              <Button
                classes={{ root: 'gps-search-upload-btn' }}
                data-testid="manual-gpx-upload-btn"
              >
                <AttachFileIcon />
                {selectedFile ? (
                  <span>{selectedFile.name}</span>
                ) : (
                  <FormattedMessageWrappedInSpan
                    id="extendedSearchGpsSearchTab.ownSearch.uploadOwnFileButton"
                    defaultMessage="Upload own GPX file"
                  />
                )}
                <input type="file" onChange={(e) => handleFileChanged(e, 'manual')} />
              </Button>
            )}
          </div>
          {/* strava upload */}
          {isStravaSearchAvailable && (
            <div
              className={classNames('gps-search-column', {
                'gps-search-column_selected': lastGpsDataInoutType === 'strava',
              })}
            >
              <Collapse in={!stravaActivityData.stravaActivityId}>
                <span className="gps-search-column-label">
                  {showStravaAuthorizationError && (
                    <FormattedMessageWrappedInSpan
                      id="extendedSearchGpsSearchTab.ownSearch.stravaErrorAuth"
                      defaultMessage="Authorization failed. Try again"
                      className="typography bold-text warning-text"
                    />
                  )}
                  {showStravaNoActivitiesError && (
                    <FormattedMessageWrappedInSpan
                      id="extendedSearchGpsSearchTab.ownSearch.stravaErrorNoActivities"
                      defaultMessage="No activities found for this account. Try to log in to another one"
                      className="typography bold-text warning-text"
                    />
                  )}
                  {!showStravaNoActivitiesError && !showStravaAuthorizationError && (
                    <FormattedMessageWrappedInSpan
                      id="extendedSearchGpsSearchTab.ownSearch.stravaFirstAction"
                      defaultMessage="Choose from Strava"
                      className="typography bold-text"
                    />
                  )}
                </span>
              </Collapse>
              {stravaAccessToken &&
                !showStravaAuthorizationError &&
                !showStravaNoActivitiesError && (
                  <StravaActivitiesSelectionQueryRenderer
                    accessToken={stravaAccessToken}
                    isAuthorizationError={showStravaAuthorizationError}
                    noStravaActivitiesFoundCallback={noStravaActivitiesFoundCallback}
                    fileSelectedManually={fileSelectedManually}
                    handleFileSelectedManuallyCallback={
                      handleFileSelectedManuallyCallback
                    }
                    setUpStravaActivity={setUpStravaActivity}
                    eventDates={props.eventDates}
                  />
                )}
              {(!stravaAccessToken || showStravaNoActivitiesError) && (
                <div>
                <img
                  src={stravaButton}
                  onClick={handleConnectToStrava}
                  data-testid="strava-gps-upload-btn"
                  className='strava-gps-upload-btn'
                  
                />
                </div>
              )}
              <Collapse
                in={Boolean(availableStravaActivityId)}
                className={classNames('small-hint-under-frame', {
                  'mt-20': Boolean(availableStravaActivityId),
                })}
              >
                <a
                  target="_blank"
                  href={`https://www.strava.com/activities/${availableStravaActivityId}`}
                >
                  <FormattedMessageWrappedInSpan
                    id="extendedSearchGpsSearchTab.ownSearch.viewOnStrava"
                    defaultMessage="View on Strava"
                    className="typography bold-text"
                  />
                </a>
              </Collapse>
            </div>
          )}
        </div>
        <Collapse
          in={!isUploading && showSearchBtn}
          className={classNames({
            'mt-20': !isUploading && showSearchBtn,
          })}
        >
          <button className="btn btn-default primary-new" onClick={handleUploadGpxFile}>
            <FormattedMessageWrappedInSpan
              id="extendedSearchGpsSearchTab.ownSearch.searchButton"
              defaultMessage="Search"
            />{' '}
            {lastGpsDataInoutType === 'strava' ? (
              <FormattedMessageWrappedInSpan
                id="extendedSearchGpsSearchTab.ownSearch.withStravabutton"
                defaultMessage="with Strava"
              />
            ) : (
              <FormattedMessageWrappedInSpan
                id="extendedSearchGpsSearchTab.ownSearch.withOwnFilebutton"
                defaultMessage="with own file"
              />
            )}
          </button>
        </Collapse>
      </div>
      <GDPRHint className="mt-30" />
    </div>
  );
};

ExtendedSearchGpsSearchTab.propTypes = {
  eventId: PropTypes.number,
  startNumber: PropTypes.string,
  onNavigateTo: PropTypes.func,
  eventDates: PropTypes.object,
};

export { ExtendedSearchGpsSearchTab };
export default createFragmentContainer(ExtendedSearchGpsSearchTab, {
  eventDates: graphql`
    fragment ExtendedSearchGpsSearchTab_eventDates on Event {
      beginsAt
      endsAt
    }
  `,
});
